import React from 'react';
import { Link } from 'react-router-dom';
import { Room } from '../types';
import { Wifi, Tv, Wind, Coffee, Users, MapPin } from 'lucide-react';

interface RoomCardProps {
  room: Room;
}

const RoomCard: React.FC<RoomCardProps> = ({ room }) => {
  const getAmenityIcon = (amenity: string) => {
    switch (amenity.toLowerCase()) {
      case 'wi-fi':
        return <Wifi className="h-4 w-4" />;
      case 'tv':
        return <Tv className="h-4 w-4" />;
      case 'air conditioning':
        return <Wind className="h-4 w-4" />;
      default:
        return <Coffee className="h-4 w-4" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group">
      <div className="relative overflow-hidden">
        <img
          src={room.image_url}
          alt={room.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4">
          <span className="bg-white bg-opacity-90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-indigo-600">
            ${room.price}/night
          </span>
        </div>
      </div>
      
      <div className="p-4 sm:p-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 line-clamp-1">
            {room.name}
          </h3>
          {!room.available && (
            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
              Unavailable
            </span>
          )}
        </div>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {room.description}
        </p>
        
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <Users className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>Up to {room.capacity} {room.capacity > 1 ? 'guests' : 'guest'}</span>
        </div>
        
        {room.amenities.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {room.amenities.slice(0, 3).map((amenity, index) => (
                <span 
                  key={index} 
                  className="inline-flex items-center text-xs bg-gray-100 px-2 py-1 rounded-md"
                >
                  {getAmenityIcon(amenity)}
                  <span className="ml-1 truncate">{amenity}</span>
                </span>
              ))}
              {room.amenities.length > 3 && (
                <span className="inline-flex items-center text-xs bg-gray-100 px-2 py-1 rounded-md">
                  +{room.amenities.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className="pt-4 border-t border-gray-100">
          <Link
            to={`/rooms/${room.id}`}
            className={`
              block w-full text-center py-2 px-4 rounded-md font-medium transition-colors duration-200
              ${room.available 
                ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {room.available ? 'View Details' : 'Currently Unavailable'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RoomCard;